import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    IconArrowLeft, IconPlayerPlayFilled, IconPlayerPauseFilled, IconDownload,
    IconPlus, IconUser, IconCheck, IconTrash, IconLoader2,
    IconEye, IconSparkles, IconPrinter, IconCopy, IconPencil, IconDots, IconAdjustmentsHorizontal,
} from '@tabler/icons-react';
import JSZip from 'jszip';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { notifications } from '@/lib/notifications';
import bulkPhotoService from '../../../services/bulkPhotoService';
import { useAuthStore } from '../../../store/authStore';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import PrintModal from '../generate-image/PrintModal';
import CustomDownloadModal from '../generate-image/CustomDownloadModal';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const POLL_MS = 2500;

export default function BulkPhotoEditDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t, currentLanguage } = useTranslation();
    const getCurrentUser = useAuthStore((s) => s.getCurrentUser);
    const isBn = currentLanguage === 'bn';

    const [project, setProject] = useState(null);
    const [themeMap, setThemeMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [zipping, setZipping] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    // Print + custom-download launched from the preview modal. Bulk image URLs
    // sit on the CORS-blocked files host, so both flows fetch their pixels via
    // the backend proxy (bulkPhotoService.downloadImage).
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printUrl, setPrintUrl] = useState(null);
    const [customDlOpen, setCustomDlOpen] = useState(false);
    const [customDlUrl, setCustomDlUrl] = useState(null);
    const [customDlId, setCustomDlId] = useState(null);
    // Inline per-image rename
    const [renamingImageId, setRenamingImageId] = useState(null);
    const [imageRenameValue, setImageRenameValue] = useState('');
    const pollRef = useRef(null);

    const toBn = (s) => (isBn ? String(s).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : String(s));
    const fmtNum = (n) => toBn(Number(n ?? 0).toLocaleString('en-US'));

    const load = useCallback(async (silent = false) => {
        try {
            const res = await bulkPhotoService.get(id);
            setProject(res.data);
        } catch (e) {
            if (!silent) notifications.show({ message: e.message, color: 'red' });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        bulkPhotoService.getThemes()
            .then((r) => setThemeMap(Object.fromEntries((r.data?.themes || []).map((th) => [th.id, th]))))
            .catch(() => {});
        load();
    }, [load]);

    useEffect(() => {
        clearInterval(pollRef.current);
        if (project?.status === 'processing') pollRef.current = setInterval(() => load(true), POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [project?.status, load]);

    // Decode a stored `theme` CSV ("skin-smoothing:medium,blemish-removal") into
    // localized tool badges. Each entry is `toolId` or `toolId:level`.
    const levelLabel = (lvl) => (lvl ? t(`bulkPhotoEdit.create.level.${lvl}`) : '');
    const themeTools = (sid) =>
        String(sid || '').split(',').map((s) => s.trim()).filter(Boolean).map((entry) => {
            const [id, level] = entry.split(':');
            const th = themeMap[id];
            return { id, name: th ? (isBn ? th.nameBn || th.name : th.name) : id, level: level || null };
        });

    const togglePause = async () => {
        setBusy(true);
        try {
            const res = await bulkPhotoService.setPaused(project.id, !project.paused);
            setProject(res.data);
        } catch (e) {
            notifications.show({ message: e.message, color: 'red' });
        } finally { setBusy(false); }
    };

    const handleDeleteProject = async () => {
        if (!window.confirm(t('bulkPhotoEdit.deleteConfirm'))) return;
        try {
            await bulkPhotoService.deleteProject(project.id);
            navigate('/documents?tab=images&sub=bulk');
        } catch (e) { notifications.show({ message: e.message, color: 'red' }); }
    };

    const downloadAll = async () => {
        const done = (project.images || []).filter((i) => i.status === 'done' && i.enhancedUrl);
        if (!done.length) { notifications.show({ message: t('bulkPhotoEdit.detail.noEnhanced'), color: 'orange' }); return; }
        setZipping(true);
        const nid = 'bpe-zip';
        notifications.show({ id: nid, message: t('bulkPhotoEdit.detail.downloadingZip'), color: 'blue', loading: true, autoClose: false });
        try {
            const zip = new JSZip();
            await Promise.all(done.map(async (img, i) => {
                const blob = await bulkPhotoService.downloadImage(img.id);
                const base = (img.name || `photo-${i + 1}`).replace(/\.[^.]+$/, '');
                zip.file(`${base}.jpg`, blob);
            }));
            const out = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(out);
            const a = document.createElement('a');
            a.href = url; a.download = `${project.name || 'bulk-photos'}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            notifications.update({ id: nid, message: t('bulkPhotoEdit.detail.downloadDone'), color: 'green', loading: false, autoClose: 2500 });
        } catch (e) {
            notifications.update({ id: nid, message: e.message, color: 'red', loading: false, autoClose: 3000 });
        } finally { setZipping(false); }
    };

    if (loading) {
        return (
            <div className="w-full font-hind py-10 flex items-center justify-center text-zinc-400">
                <IconLoader2 size={24} className="animate-spin" />
            </div>
        );
    }
    if (!project) return null;

    const total = project.photoCount;
    const done = project.doneCount;
    // While the batch is actively generating, lock download/per-image actions.
    const generating = project.status === 'processing';
    // Date + start time the project was created (e.g. "14 July, 2026 · 4:32 PM").
    const createdAt = new Date(project.createdAt);
    const dateLabel = new Intl.DateTimeFormat(isBn ? 'bn-BD' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(createdAt);
    const timeLabel = new Intl.DateTimeFormat(isBn ? 'bn-BD' : 'en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).format(createdAt);

    const headerBadge = project.status === 'completed'
        ? { label: t('bulkPhotoEdit.status.completed'), cls: 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30' }
        : project.status === 'paused'
            ? { label: t('bulkPhotoEdit.status.paused', { done: fmtNum(done), total: fmtNum(total) }), cls: 'text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/30' }
            : { label: t('bulkPhotoEdit.status.running', { done: fmtNum(done), total: fmtNum(total) }), cls: 'text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30' };

    const thumb = (img) => (img.status === 'done' ? img.enhancedUrl : img.originalUrl) || img.originalUrl;

    // Previewable images mapped to the shared ImagePreviewModal shape.
    const viewable = (project.images || [])
        .filter((im) => im.enhancedUrl || im.originalUrl)
        .map((im) => ({ id: im.id, aiImage: im.enhancedUrl || im.originalUrl, fileName: im.name }));
    const openLightbox = (imgId) => {
        const found = viewable.find((v) => v.id === imgId);
        if (found) setPreviewImage(found);
    };

    // Download via the backend proxy (avoids R2 CORS on direct fetch).
    const downloadPreviewImage = async (img) => {
        try {
            const blob = await bulkPhotoService.downloadImage(img.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(img.fileName || img.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            notifications.show({ message: t('imageGeneration.imageDownloaded'), color: 'green' });
        } catch {
            notifications.show({ message: t('imageGeneration.downloadFailed'), color: 'red' });
        }
    };

    // ── Per-image card actions (⋯ menu) ────────────────────────────────────────
    const downloadOne = (img) => downloadPreviewImage(img);

    const printOne = async (img) => {
        try {
            const blob = await bulkPhotoService.downloadImage(img.id);
            setPrintUrl(URL.createObjectURL(blob));
            setShowPrintModal(true);
        } catch {
            notifications.show({ message: t('imageGeneration.downloadFailed'), color: 'red' });
        }
    };

    const copyOne = async (img) => {
        try {
            const blob = await bulkPhotoService.downloadImage(img.id);
            // Clipboard image write needs PNG — repaint through a canvas.
            const bmp = await createImageBitmap(blob);
            const canvas = document.createElement('canvas');
            canvas.width = bmp.width; canvas.height = bmp.height;
            canvas.getContext('2d').drawImage(bmp, 0, 0);
            const png = await new Promise((res) => canvas.toBlob(res, 'image/png'));
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
            notifications.show({ message: t('imageGeneration.imageCopied') || 'Copied', color: 'green' });
        } catch {
            notifications.show({ message: t('imageGeneration.downloadFailed'), color: 'red' });
        }
    };

    const customDownloadOne = (img) => {
        setCustomDlUrl(img.enhancedUrl || img.originalUrl);
        setCustomDlId(img.id);
        setCustomDlOpen(true);
    };

    const regenerateOne = async (img) => {
        try {
            const res = await bulkPhotoService.regenerateImage(img.id);
            setProject(res.data); // status flips to processing → the poll effect resumes
            getCurrentUser().catch(() => {}); // a regenerate is paid for up front: refresh the shown balance
            notifications.show({ message: t('bulkPhotoEdit.detail.regenStarted'), color: 'blue' });
        } catch (e) {
            notifications.show({ message: e.code === 'INSUFFICIENT_CREDITS' ? t('bulkPhotoEdit.create.insufficientCredits') : e.message, color: 'red' });
        }
    };

    const deleteOne = async (img) => {
        if (!window.confirm(t('bulkPhotoEdit.detail.deleteImageConfirm'))) return;
        try {
            await bulkPhotoService.deleteImage(img.id);
            // Optimistic removal for instant feedback, then reload to sync header counts.
            setProject((prev) => (prev ? { ...prev, images: prev.images.filter((x) => x.id !== img.id) } : prev));
            load(true);
            getCurrentUser().catch(() => {}); // an unfinished photo's credit is refunded
            notifications.show({ message: t('bulkPhotoEdit.detail.imageDeleted'), color: 'green' });
        } catch (e) {
            notifications.show({ message: e.message, color: 'red' });
        }
    };

    const startRenameImage = (img) => { setRenamingImageId(img.id); setImageRenameValue(img.name || ''); };
    const cancelRenameImage = () => { setRenamingImageId(null); setImageRenameValue(''); };
    const saveRenameImage = async (img) => {
        const name = imageRenameValue.trim();
        if (!name || name === img.name) { cancelRenameImage(); return; }
        try {
            await bulkPhotoService.renameImage(img.id, name);
            setProject((prev) => (prev ? { ...prev, images: prev.images.map((x) => (x.id === img.id ? { ...x, name } : x)) } : prev));
        } catch (e) {
            notifications.show({ message: e.message, color: 'red' });
        } finally { cancelRenameImage(); }
    };

    return (
        <div className="w-full font-hind space-y-4 py-2 sm:py-3">
            {/* Compact top row: back arrow merged into the breadcrumb. The current
                project is dropped from the crumb since the H1 right below repeats it. */}
            <div className="flex items-center gap-2 text-[12px] text-zinc-500 dark:text-zinc-400 min-w-0">
                <button onClick={() => navigate('/documents?tab=images&sub=bulk')} title={t('bulkPhotoEdit.detail.backToProjects')}
                    className="w-7 h-7 -ml-1 rounded-lg flex items-center justify-center hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0">
                    <IconArrowLeft size={16} stroke={2.1} />
                </button>
                <Link to="/documents" className="hover:text-primary transition-colors">{t('navigation.myDocuments')}</Link>
                <span className="text-zinc-300 dark:text-zinc-600">/</span>
                <Link to="/documents?tab=images&sub=bulk" className="hover:text-primary transition-colors">{t('navigation.bulkPhotoEdit')}</Link>
            </div>

            {/* Header — title + inline status and a single-line meta row on the left,
                actions on the right. Kept to two tight lines to reclaim vertical space. */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight truncate min-w-0">{project.name}</h1>
                        <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${headerBadge.cls}`}>{headerBadge.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[12px] text-zinc-400 dark:text-zinc-500">
                        <span>{dateLabel} · {timeLabel} · {t('bulkPhotoEdit.photoMeta', { count: fmtNum(total) })}</span>
                    </div>
                    {/* Applied styles/options for the batch — shown once here as light badges. */}
                    {project.theme && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {themeTools(project.theme).map((tool) => (
                                <span key={tool.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
                                    {tool.name}{tool.level && <span className="ml-1 text-primary font-semibold">· {levelLabel(tool.level)}</span>}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    {project.status !== 'completed' && (
                        <button onClick={togglePause} disabled={busy} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold rounded-lg transition-colors disabled:opacity-50">
                            {project.paused ? <><IconPlayerPlayFilled size={12} />{t('bulkPhotoEdit.detail.resume')}</> : <><IconPlayerPauseFilled size={12} />{t('bulkPhotoEdit.detail.pause')}</>}
                        </button>
                    )}
                    <button onClick={downloadAll} disabled={zipping || generating} title={generating ? t('bulkPhotoEdit.detail.processing') : undefined} className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-[12.5px] font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-zinc-900">
                        <IconDownload size={14} stroke={1.8} />{t('bulkPhotoEdit.detail.downloadAll', { count: fmtNum(project.totals?.enhanced || 0) })}
                    </button>
                    <button onClick={() => navigate(`/bulk-photo-edit/new?projectId=${project.id}`)} className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-white text-[12.5px] font-semibold rounded-lg transition-colors">
                        <IconPlus size={14} stroke={2.2} />{t('bulkPhotoEdit.detail.addPhotos')}
                    </button>
                    <button onClick={handleDeleteProject} title={t('bulkPhotoEdit.detail.deleteProject')} className="w-9 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:text-red-500 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0">
                        <IconTrash size={15} />
                    </button>
                </div>
            </div>

            {/* Status banner */}
            {project.paused && project.status !== 'completed' && (
                <div className="flex items-center gap-3.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/60 rounded-xl px-4 py-3.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0" />
                    <span className="flex-1 text-[13.5px] font-medium text-amber-800 dark:text-amber-300">{t('bulkPhotoEdit.detail.pausedBanner', { done: fmtNum(done), total: fmtNum(total) })}</span>
                    <button onClick={togglePause} disabled={busy} className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0 disabled:opacity-50">
                        <IconPlayerPlayFilled size={12} />{t('bulkPhotoEdit.detail.resume')}
                    </button>
                </div>
            )}
            {!project.paused && project.status === 'processing' && (
                <div className="flex items-center gap-3.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/60 rounded-xl px-4 py-3.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0 animate-pulse" />
                    <span className="flex-1 text-[13.5px] font-medium text-blue-800 dark:text-blue-300">{t('bulkPhotoEdit.detail.processingBanner', { done: fmtNum(done), total: fmtNum(total) })}</span>
                </div>
            )}

            {/* Per-photo grid — card layout with a ⋯ actions menu (mirrors AI Photo Edit) */}
            <div>
                <div className="columns-2 md:columns-3 xl:columns-4 gap-4">
                    {(project.images || []).map((img) => {
                        const isDone = img.status === 'done';
                        const isProcessing = img.status === 'processing';
                        const src = thumb(img);
                        const canPreview = isDone && (img.enhancedUrl || img.originalUrl);
                        return (
                            <div key={img.id} className="mb-4 break-inside-avoid group rounded-2xl overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-sm">
                                {/* Image + status badges */}
                                <div className="relative bg-zinc-100 dark:bg-zinc-800">
                                    {src ? (
                                        <button type="button" disabled={!canPreview} onClick={() => canPreview && openLightbox(img.id)}
                                            className={`block w-full ${canPreview ? 'cursor-zoom-in' : 'cursor-default'}`}>
                                            <img src={src} alt="" className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]" style={{ opacity: isDone ? 1 : 0.7 }} draggable="false" />
                                        </button>
                                    ) : (
                                        <div className="relative aspect-square flex items-center justify-center">
                                            <IconUser size={54} stroke={1} className="text-zinc-300 dark:text-zinc-600" />
                                        </div>
                                    )}
                                    {isDone && !img.error && (
                                        <span className="absolute top-2.5 right-2.5 w-[26px] h-[26px] rounded-full bg-emerald-600 flex items-center justify-center shadow-md"><IconCheck size={14} stroke={3} className="text-white" /></span>
                                    )}
                                    {isProcessing && (
                                        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-blue-600/85 px-2.5 py-1 rounded-lg"><IconLoader2 size={11} className="animate-spin" />{t('bulkPhotoEdit.detail.processing')}</span>
                                    )}
                                    {img.status === 'queued' && (
                                        <span className="absolute top-2.5 left-2.5 text-[11px] font-semibold text-white bg-zinc-900/65 px-2.5 py-1 rounded-lg">{t('bulkPhotoEdit.detail.waiting')}</span>
                                    )}
                                    {/* This image's applied options as light badges — revealed on hover */}
                                    {(img.theme || project.theme) && (
                                        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] flex flex-wrap gap-1 p-2 pt-8 bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                            {themeTools(img.theme || project.theme).map((tool) => (
                                                <span key={tool.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/95 dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700 shadow-sm text-[10px] font-medium text-zinc-700 dark:text-zinc-300">
                                                    {tool.name}{tool.level && <span className="ml-1 text-primary font-semibold">· {levelLabel(tool.level)}</span>}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Footer: filename (+ style) and the ⋯ menu */}
                                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                                    {renamingImageId === img.id ? (
                                        <input autoFocus value={imageRenameValue}
                                            onChange={(e) => setImageRenameValue(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') saveRenameImage(img); if (e.key === 'Escape') cancelRenameImage(); }}
                                            onBlur={() => saveRenameImage(img)}
                                            className="min-w-0 flex-1 px-1.5 py-0.5 rounded border border-primary/60 bg-white dark:bg-zinc-800 text-[12px] font-mono text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-primary/40" />
                                    ) : (
                                        <div className="min-w-0 flex-1">
                                            <span
                                                className="block text-[11.5px] text-zinc-600 dark:text-zinc-300 font-mono truncate cursor-pointer select-none"
                                                title={t('imageGeneration.doubleClickToRename')}
                                                onDoubleClick={() => startRenameImage(img)}
                                            >{img.name}</span>
                                        </div>
                                    )}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={generating}>
                                            <button disabled={generating} className="w-7 h-7 rounded-lg flex items-center justify-center text-[#6E6A63] hover:text-[#1C1B19] bg-[#f5daa3] flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" title={generating ? t('bulkPhotoEdit.detail.processing') : t('bulkPhotoEdit.detail.actions', { defaultValue: 'Actions' })}>
                                                <IconDots size={15} />
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="z-[10002] w-52" onCloseAutoFocus={(e) => e.preventDefault()}>
                                            {isDone ? (
                                                <>
                                                    {canPreview && (
                                                        <DropdownMenuItem onClick={() => openLightbox(img.id)}><IconEye size={14} className="mr-2" />{t('imageGeneration.previewTooltip')}</DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuLabel>{t('imageGeneration.downloadOptions')}</DropdownMenuLabel>
                                                    <DropdownMenuItem onClick={() => downloadOne(img)}><IconDownload size={14} className="mr-2" />{t('imageGeneration.downloadOriginal')}</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => customDownloadOne(img)}><IconAdjustmentsHorizontal size={14} className="mr-2" />{t('imageGeneration.customSizeQuality')}</DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => regenerateOne(img)}><IconSparkles size={14} className="mr-2" />{t('bulkPhotoEdit.detail.regenerate')}</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => printOne(img)}><IconPrinter size={14} className="mr-2" />{t('imageGeneration.printImage')}</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => copyOne(img)}><IconCopy size={14} className="mr-2" />{t('imageGeneration.copyImage')}</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => startRenameImage(img)}><IconPencil size={14} className="mr-2" />{t('common.rename')}</DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => deleteOne(img)} className="text-red-600 focus:text-red-600"><IconTrash size={14} className="mr-2" />{t('common.delete')}</DropdownMenuItem>
                                                </>
                                            ) : (
                                                <>
                                                    <DropdownMenuItem onClick={() => startRenameImage(img)}><IconPencil size={14} className="mr-2" />{t('common.rename')}</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => deleteOne(img)} className="text-red-600 focus:text-red-600"><IconTrash size={14} className="mr-2" />{t('common.delete')}</DropdownMenuItem>
                                                </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Stats footer — internal cost/token metrics, hidden from the user panel */}

            <ImagePreviewModal
                opened={!!previewImage}
                onClose={() => setPreviewImage(null)}
                images={viewable}
                initialImage={previewImage}
                onDownloadImage={downloadPreviewImage}
                onGetShareBlob={(img) => bulkPhotoService.downloadImage(img.id)}
                onDelete={(img) => bulkPhotoService.deleteImage(img.id)}
                onImageDeleted={() => load(true)}
                onPrint={async (imageUrl, imageId) => {
                    try {
                        // Adjusted prints arrive as canvas blob URLs (already CORS-safe);
                        // otherwise fetch the pixels through the backend proxy.
                        let url = imageUrl;
                        if (!String(url || '').startsWith('blob:')) {
                            const blob = await bulkPhotoService.downloadImage(imageId);
                            url = URL.createObjectURL(blob);
                        }
                        setPrintUrl(url);
                        setShowPrintModal(true);
                    } catch {
                        notifications.show({ message: t('imageGeneration.downloadFailed'), color: 'red' });
                    }
                }}
                onCustomDownload={(imageUrl, imageId) => {
                    setCustomDlUrl(imageUrl);
                    setCustomDlId(imageId);
                    setCustomDlOpen(true);
                }}
                hideEdit
            />

            <PrintModal
                opened={showPrintModal}
                onClose={() => {
                    setShowPrintModal(false);
                    if (printUrl?.startsWith('blob:')) URL.revokeObjectURL(printUrl);
                    setPrintUrl(null);
                }}
                imageUrl={printUrl}
                imageId={null}
                initialSizeType="free"
            />

            <CustomDownloadModal
                opened={customDlOpen}
                onClose={() => setCustomDlOpen(false)}
                imageUrl={customDlUrl}
                imageId={customDlId}
                getBlob={(imgId) => bulkPhotoService.downloadImage(imgId)}
            />

        </div>
    );
}
